import React from 'react';
import { sortedIndexBy } from 'lodash';
import compose from 'lodash/fp/compose';
import getDay from 'date-fns/getDay';
import getHours from 'date-fns/getHours';
import addDays from 'date-fns/fp/addDays';
import setHours from 'date-fns/fp/setHours';
import setMinutes from 'date-fns/fp/setMinutes';
import { Button, Modal } from '@chaser/components';
import {
  Text,
  Stack,
  Box,
  ButtonGroup,
  List,
  TextLink,
  Tooltip,
  Position,
} from '@chaser/components';
import useOrganisation from '../../../../hooks/useOrganisation';
import { formatTime, formatDate } from '../../../../util/time';
import usePush from '../../../../hooks/usePush';
import OrganisationService from '../../../../services/organisation-service';
import { useCacheActions } from '../../../../hooks/useFetch';
import useCompleteAction, { Actions } from '../../../../hooks/useCompleteAction';
import { roles } from '@/util/constants';
import RoleGate from '@/components/FeatureGate/RoleGate';
import useShortCode from '../../../../hooks/useShortCode';

const getNextChaseDate = scheduleTimes => {
  const now = new Date();
  const currentDay = getDay(now);
  const currentHour = getHours(now);

  const index = sortedIndexBy(scheduleTimes, { day: currentDay }, o => o.day);
  const { day: nextWeekChaseDay, hour: nextWeekChaseHour } =
    scheduleTimes[index] || scheduleTimes[0];

  let dayDifference = nextWeekChaseDay - currentDay;
  if (dayDifference < 0 || (dayDifference === 0 && currentHour >= nextWeekChaseHour)) {
    dayDifference += 7;
  }
  return compose(addDays(dayDifference), setHours(nextWeekChaseHour), setMinutes(0))(now);
};

const ResumeInfoModal = ({
  open,
  close,
  onClosed,
  hasChasedBefore = false,
  shouldShowResume = true,
}) => {
  const shortCode = useShortCode();
  const { execute, loading } = usePush(OrganisationService.resumeChasing, {
    errorToast: true,
  });

  const { invalidateDomain } = useCacheActions();
  const completeAction = useCompleteAction();

  async function resume() {
    const [err] = await execute(shortCode);
    invalidateDomain('organisation');
    if (err) return;
    completeAction(Actions.START_CHASING);
    close(true);
  }

  const { scheduleTimes, timezone } = useOrganisation();
  const nextChaseDate = getNextChaseDate(scheduleTimes);

  return (
    <Modal
      open={open}
      toggle={close}
      onClosed={onClosed}
      title={`What happens if I ${hasChasedBefore ? 'resume' : 'start'} sending reminders?`}
      body={
        <Box py="medium">
          <Stack>
            <Text trim={false}>
              On <strong>{formatDate(nextChaseDate, { year: false, weekday: 'long' })}</strong> at{' '}
              <strong>{formatTime(nextChaseDate)}</strong> ({timezone}) we will begin chasing any
              unpaid invoices currently marked as <strong>Chasing</strong>. If your scheduled chase
              is more than 24 hours away and you've activated forewarn notifications, you'll receive
              at least one advance reminder email. To manage your invoices:
            </Text>
            <List type="ordered">
              <Text as="li" trim={false}>
                Review outstanding invoices in the{' '}
                <TextLink as="a" target="_blank" href="https://help.chaserhq.com/video-recevables">
                  Receivables
                </TextLink>{' '}
                section
              </Text>
              <Text as="li" trim={false}>
                Stop chasing specific customers or invoices if needed in{' '}
                <TextLink as="a" target="_blank" href="https://help.chaserhq.com/video-recevables">
                  Receivables
                </TextLink>{' '}
                section
              </Text>
              <Text as="li" trim={false}>
                Adjust your scheduled chase day and time in{' '}
                <TextLink as="a" target="_blank" href="https://help.chaserhq.com/set-chasing-times">
                  Manage, Timings
                </TextLink>
              </Text>
              <Text as="li" trim={false}>
                Update your{' '}
                <TextLink
                  as="a"
                  target="_blank"
                  href="https://help.chaserhq.com/add-and-edit-a-schedule"
                >
                  Schedules
                </TextLink>{' '}
                and{' '}
                <TextLink
                  as="a"
                  target="_blank"
                  href="https://help.chaserhq.com/create-and-manage-templates"
                >
                  Templates
                </TextLink>{' '}
                to reflect your preferences.
              </Text>
            </List>
          </Stack>
        </Box>
      }
      footer={
        <ButtonGroup>
          <Button appearance="secondary" onClick={() => close(false)}>
            Close
          </Button>
          {shouldShowResume && (
            <RoleGate
              roles={[roles.owner, roles.manager, roles.cashflowManager]}
              renderGate={(_, children) => {
                return (
                  <Tooltip
                    position={Position.BOTTOM}
                    content="This action needs additional permissions. Please contact your organisation owner to review your permissions."
                  >
                    {React.cloneElement(children, { disabled: true })}
                  </Tooltip>
                );
              }}
            >
              <Button loading={loading} onClick={resume}>
                {hasChasedBefore ? 'Resume sending reminders' : 'Start sending reminders'}
              </Button>
            </RoleGate>
          )}
        </ButtonGroup>
      }
    />
  );
};

export default ResumeInfoModal;
